import { Component, DestroyRef, Input, output, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FileModel } from '@core/models/file.model';
import { FileService } from '@core/services/file.service';
import { ImageService } from '@core/services/image/image.service';
import { ImagePreviewPipe } from '@ux/pipes/image-preview.pipe';
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2';

import { ContextMenuItemComponent } from '../context-menu/context-menu-item/context-menu-item.component';
import { ContextMenuComponent } from '../context-menu/context-menu.component';

interface PreviewImageState {
  image: FileModel | null;
  isShowMenu: boolean;
  url: string;
  downloadUrl: string;
}

@Component({
  selector: 'ux-preview-image',
  templateUrl: './preview-image.component.html',
  styleUrls: ['./preview-image.component.less'],
  imports: [ContextMenuComponent, ContextMenuItemComponent, NgClickOutsideDelayOutsideDirective],
  standalone: true,
})
export class PreviewImageComponent {
  private readonly imagePreviewPipe = inject(ImagePreviewPipe);
  private readonly fileService = inject(FileService);
  readonly imageService = inject(ImageService);
  private readonly destroyRef = inject(DestroyRef);

  readonly deleteImage = output<FileModel>();

  private readonly state = signal<PreviewImageState>({
    image: null,
    isShowMenu: false,
    url: '',
    downloadUrl: '',
  });

  protected readonly isShowMenu = computed(() => this.state().isShowMenu);
  protected readonly url = computed(() => this.state().url);
  protected readonly downloadUrl = computed(() => this.state().downloadUrl);
  protected readonly currentImage = computed(() => this.state().image);

  @Input()
  set image(value: FileModel) {
    if (!value) {
      return;
    }

    const url = this.imagePreviewPipe.transform(value, 150, 150);
    const downloadUrl = this.imagePreviewPipe.transform(value, false, false);

    this.state.update(state => ({
      ...state,
      image: value,
      url,
      downloadUrl,
    }));
  }

  onDownloadFile(): void {
    if (!this.state().downloadUrl) {
      return;
    }

    this.fileService.getBlobFromUrl(this.state().downloadUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  onToggleMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: !state.isShowMenu,
    }));
  }

  onCloseMenu(): void {
    this.state.update(state => ({
      ...state,
      isShowMenu: false,
    }));
  }

  onDeleteImage(): void {
    if (this.currentImage()) {
      this.deleteImage.emit(this.currentImage());
      this.onCloseMenu();
    }
  }
}
